import React, { useState, useEffect } from 'react';
import { db } from '../../services/firebase';
import { collection, onSnapshot, query, orderBy, deleteDoc, doc, addDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import styles from '../../styles/AdminStyles/AdminWaitingList.module.css';
import SeatCustomerModal from './SeatCustomerModal';
import { useNavigate } from 'react-router-dom';

const AdminWaitingList = () => {
    const [waitingList, setWaitingList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [modalMode, setModalMode] = useState('seat'); // 'seat' or 'notify'
    const navigate = useNavigate();

    useEffect(() => {
        const q = query(collection(db, 'waitingList'), orderBy('createdAt'));
        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setWaitingList(list);
                setLoading(false);
            },
            (err) => {
                console.error("Error fetching waiting list:", err);
                setError("Failed to load waiting list.");
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    const handleOpenNotifyModal = (customer) => {
        setSelectedCustomer(customer);
        setModalMode('notify');
        setIsModalOpen(true);
    };

    const handleOpenSeatModal = (customer) => {
        setSelectedCustomer(customer);
        setModalMode('seat');
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedCustomer(null);
    };

    const handleConfirmModal = async (tableNumber) => {
        if (!selectedCustomer) return;

        if (modalMode === 'notify') {
            const message = `Hello, this is Grey Coffee.\nYour table is now ready — please head to Table Number: ${tableNumber}.\nWe're excited to welcome you!\n\nمرحبًا، معك غري كوفي.\nتم تجهيز طاولتك الآن — يرجى التوجه إلى الطاولة رقم: ${tableNumber}.\nنتطلع إلى خدمتك!`;
            // Clean phone number: remove all non-digit characters
            const cleanPhone = (selectedCustomer.phone || '').replace(/\D/g, '');
            const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, 'whatsapp_notification_tab');

            const customerRef = doc(db, 'waitingList', selectedCustomer.id);
            await updateDoc(customerRef, {
                notified: true,
                notifiedAt: serverTimestamp(),
                assignedTable: tableNumber, // Store the assigned table
            });

        } else if (modalMode === 'seat') {
            try {
                await addDoc(collection(db, 'seatedCustomers'), {
                    ...selectedCustomer,
                    tableNumber: tableNumber,
                    seatedAt: serverTimestamp()
                });
                await deleteDoc(doc(db, 'waitingList', selectedCustomer.id));
            } catch (err) {
                console.error("Error seating customer: ", err);
                alert("Failed to seat customer. Please try again.");
            }
        }

        handleCloseModal();
    };

    // Deduplicate waitingList by id to avoid duplicate React keys
    const uniqueWaitingList = Array.from(new Map(waitingList.map(item => [item.id, item])).values());

    if (loading) {
        return <div className={styles.centered}>Loading waiting list...</div>;
    }

    if (error) {
        return <div className={`${styles.centered} ${styles.error}`}>{error}</div>;
    }

    return (
        <div className={styles.container}>
            <button onClick={() => navigate('/admin/seated-list')} className={styles.navigateButton} style={{marginBottom: 16, padding: '8px 16px', fontWeight: 600, borderRadius: 6, background: '#e3e8f0', border: 'none', cursor: 'pointer'}}>Go to Seating List</button>
            <h1 className={styles.title}>Customer Waiting List</h1>
            {uniqueWaitingList.length === 0 ? (
                <p>The waiting list is currently empty.</p>
            ) : (
                <>
                {/* Desktop Table */}
                <div className={styles.tableContainer + ' ' + styles.hideOnMobile}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Phone</th>
                                <th>Party Size</th>
                                <th>Time Added</th>
                                <th>Assigned Table</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uniqueWaitingList.map((entry, index) => (
                                <tr key={entry.id} className={entry.notified ? styles.notifiedRow : ''}>
                                    <td>{index + 1}</td>
                                    <td>{entry.name}</td>
                                    <td>{entry.phone}</td>
                                    <td>{entry.numPeople}</td>
                                    <td>{entry.createdAt?.toDate().toLocaleTimeString()}</td>
                                    <td>{entry.assignedTable || 'N/A'}</td>
                                    <td className={styles.actionsCell}>
                                        <button
                                            onClick={() => handleOpenNotifyModal(entry)}
                                            className={`${styles.actionButton} ${styles.notifyButton}`}
                                            disabled={entry.notified}
                                        >
                                            {entry.notified ? 'Notified' : 'Notify'}
                                        </button>
                                        <button
                                            onClick={() => handleOpenSeatModal(entry)}
                                            className={`${styles.actionButton} ${styles.seatButton}`}
                                        >
                                            Seat
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* Mobile Cards */}
                <div className={styles.mobileList + ' ' + styles.showOnMobile}>
                  {uniqueWaitingList.map((entry, index) => (
                    <div key={entry.id} className={styles.mobileCard + ' ' + (entry.notified ? styles.notifiedRow : '')}>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>#{index + 1}</span></div>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>Name:</span> {entry.name}</div>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>Phone:</span> {entry.phone}</div>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>Party Size:</span> {entry.numPeople}</div>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>Time:</span> {entry.createdAt?.toDate().toLocaleTimeString()}</div>
                      <div className={styles.mobileCardRow}><span className={styles.mobileCardLabel}>Table:</span> {entry.assignedTable || 'N/A'}</div>
                      <div className={styles.mobileCardActions}>
                        <button
                          onClick={() => handleOpenNotifyModal(entry)}
                          className={`${styles.actionButton} ${styles.notifyButton}`}
                          disabled={entry.notified}
                        >
                          {entry.notified ? 'Notified' : 'Notify'}
                        </button>
                        <button
                          onClick={() => handleOpenSeatModal(entry)}
                          className={`${styles.actionButton} ${styles.seatButton}`}
                        >
                          Seat
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                </>
            )}
             {isModalOpen && (
                <SeatCustomerModal 
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmModal}
                    mode={modalMode}
                />
            )}
        </div>
    );
};

export default AdminWaitingList; 